/**
 * Nosto-CarouselPLP.js
 *
 * PLP independiente:
 * - 8 productos principales por request;
 * - 24 productos por página lógica;
 * - cada hit genera hero + carrusel de productos relacionados por color.
 */
(function initializeNostoCarouselPlp(global) {
    "use strict";

    const SELECTORS = {
        root: "[data-nosto-carousel-plp]",
        results: "[data-nosto-carousel-plp-results]",
        filters: "[data-nosto-plp-filters]",
        sort: "[data-nosto-plp-sort]",
        loadMore: "[data-nosto-carousel-plp-load-more]",
        pagination: "[data-nosto-carousel-pagination]",
        applyFilters: "[data-nosto-plp-apply-filters]",
        clearFilters: "[data-nosto-plp-clear-filters]",
    };

    const PRODUCTS_PER_REQUEST = 8;
    /**
     * Rollback rápido de la paginación híbrida.
     *
     * false: scroll infinito puro (NostoPlpController).
     * true: scroll infinito + paginación al completar cada página
     *       lógica (NostoHybridPlpController + NostoHybridPaginationManager).
     */
    const ENABLE_HYBRID_PAGINATION = false;

    const PRODUCTS_PER_LOGICAL_PAGE = 24;
    const LOGICAL_PAGE_PARAM = "nostoPage";

    function validateDependencies() {
        const dependencies = [
            "NostoClient",
            "NostoFacetManager",
            "NostoHitStore",
            "NostoProductResultsRenderer",
            "NostoFacetRenderer",
            "NostoProductViewService",
            "NostoQuickBuyModal",
            "NostoPlpController",
            "NostoHybridPlpController",
            "NostoHybridPaginationManager",
            "NostoCarouselProductBlock",
        ];

        const missing = dependencies.filter(
            (name) => typeof global[name] !== "function"
        );

        if (missing.length) {
            throw new Error(
                `No fue posible inicializar el PLP carrusel. Faltan: ${missing.join(", ")}.`
            );
        }
    }

    function getPageConfig(root) {
        return {
            categoryId:
                root.dataset.nostoCategoryId ||
                global.NOSTO_PAGE_CONTEXT?.categoryId ||
                "",

            categoryPath:
                root.dataset.nostoCategoryPath ||
                global.NOSTO_PAGE_CONTEXT?.categoryPath ||
                "",

            debug:
                root.dataset.nostoDebug === "true" ||
                global.NOSTO_DEBUG === true,
        };
    }

    function createFacetManager(config) {
        const manager = new global.NostoFacetManager({
            definitions: global.NOSTO_FACET_DEFINITIONS || [],
            colorGroups: global.NOSTO_COLOR_GROUPS || {},
            colorFields: ["customFields.custom-color"],
            priceField: "price",
            syncUrl: true,
            urlMode: "replace",
            debug: config.debug,
        });

        if (manager.hasManagedUrlState()) {
            manager.restoreStateFromUrl(global.location.href, {
                emit: false,
                syncUrl: false,
            });
        }

        return manager;
    }

    function createProductViewService(config) {
        return new global.NostoProductViewService({
            cardView: "product-card",
            quickBuyView: "nosto-quick-buy",
            concurrency: 4,
            cache: true,
            debug: config.debug,
        });
    }

    function createQuickBuyModal({
        productViewService,
        config,
    }) {
        if (global.NOSTO_QUICK_BUY_MODAL) {
            return global.NOSTO_QUICK_BUY_MODAL;
        }

        const modal = new global.NostoQuickBuyModal({
            viewService: productViewService,
            debug: config.debug,
        });

        modal.init();
        global.NOSTO_QUICK_BUY_MODAL = modal;

        return modal;
    }

    function createFacetRenderer({
        facetManager,
        config,
    }) {
        return new global.NostoFacetRenderer({
            container: SELECTORS.filters,
            facetManager,
            labels: global.NOSTO_FACET_LABELS || {},
            hiddenFields: ["customFields.shasa-color"],
            colorField: "customFields.custom-color",
            priceField: "price",
            sizeField: "skus.customFields.talla",
            sizeOrder: global.NOSTO_SIZE_ORDER || [],
            fieldOrder: [
                "skus.customFields.talla",
                "customFields.custom-color",
                "customFields.custom-fits",
                "customFields.custom-product_group_code",
                "customFields.custom-pg_filtro",
                "customFields.custom-material",
                "price",
            ],
            applyButton: SELECTORS.applyFilters,
            clearButton: SELECTORS.clearFilters,
            autoApply: false,
            defaultOpen: true,
            currency: global.Shopify?.currency?.active || "MXN",
            currencySymbol: "$",
            showCounts: true,
            showPriceLabels: false,
            debug: config.debug,
        });
    }

    function createResultsRenderer({
        productViewService,
        getController,
        config,
    }) {
        return new global.NostoProductResultsRenderer({
            container: SELECTORS.results,
            placeholderTagName: "div",
            statusTagName: "div",
            placeholderClass: "nosto-product-placeholder",
            productItemClass: "nosto-carousel-plp__item",
            emptyMessage:
                "No encontramos productos con los filtros seleccionados.",
            errorMessage:
                "No fue posible cargar los productos. Intenta nuevamente.",
            debug: config.debug,

            productCardFactory: async (options) => {
                const block =
                    new global.NostoCarouselProductBlock({
                        ...options,
                        viewService: productViewService,
                        viewName:
                            "nosto-carousel-block",

                        onProductClick({
                            productId,
                            hit,
                        }) {
                            return getController()
                                ?.recordProductClick(
                                    productId,
                                    {
                                        fallbackHit: hit,
                                    }
                                );
                        },
                    });

                await block.render();
                return block;
            },
        });
    }

    function bindFiltersDrawerClose() {
        const drawer = document.querySelector(
            "[data-collection-sidebar]"
        );

        const trigger = document.querySelector(
            '[aria-controls^="filter-groups-"]'
        );

        if (!drawer || !trigger) return;

        document.addEventListener("click", (event) => {
            const closeControl = event.target.closest(
                "[data-collection-sidebar-close]"
            );

            if (!closeControl) return;

            event.preventDefault();

            if (
                trigger.getAttribute("aria-expanded") ===
                "true"
            ) {
                trigger.click();
            }
        });
    }


    async function init() {
        const root = document.querySelector(
            SELECTORS.root
        );

        if (!root) return null;

        if (
            root.dataset.nostoInitialized === "true" ||
            root.dataset.nostoInitializing === "true"
        ) {
            return global.NOSTO_CAROUSEL_PLP || null;
        }

        root.dataset.nostoInitializing = "true";
        validateDependencies();

        const config = getPageConfig(root);
        const client = new global.NostoClient({
            debug: config.debug,
        });

        const facetManager = createFacetManager(config);
        const hitStore = new global.NostoHitStore({
            debug: config.debug,
        });

        const productViewService =
            createProductViewService(config);

        const quickBuyModal =
            createQuickBuyModal({
                productViewService,
                config,
            });

        const facetRenderer =
            createFacetRenderer({
                facetManager,
                config,
            });

        const paginationManager =
            ENABLE_HYBRID_PAGINATION
                ? new global.NostoHybridPaginationManager({
                container: SELECTORS.pagination,
                pageParam: LOGICAL_PAGE_PARAM,
                loadedParam: "loaded",
                logicalPageSize:
                    PRODUCTS_PER_LOGICAL_PAGE,
                debug: config.debug,
            })
                : null;

        paginationManager?.hide();

        let controller = null;

        const resultsRenderer =
            createResultsRenderer({
                productViewService,
                getController() {
                    return controller;
                },
                config,
            });

        controller =
            new (ENABLE_HYBRID_PAGINATION
                ? global.NostoHybridPlpController
                : global.NostoPlpController)({
                categoryId: config.categoryId,
                categoryPath: config.categoryPath,
                client,
                facetManager,
                hitStore,
                renderer: resultsRenderer,
                sortElement: SELECTORS.sort,
                loadMoreElement: SELECTORS.loadMore,
                pageSize: PRODUCTS_PER_REQUEST,
                logicalPageSize:
                    PRODUCTS_PER_LOGICAL_PAGE,
                logicalPageParam:
                    LOGICAL_PAGE_PARAM,

                onHybridPaginationChange(state) {
                    paginationManager?.render(state);
                },

                productFields: [
                    "name",
                    "url",
                    "imageUrl",
                ],

                track: "category",
                redirect: true,
                syncPaginationToUrl: true,
                restoreLoadedProducts: true,
                restoreScroll: true,
                debug: config.debug,

                onFacets(facets) {
                    facetRenderer.render(facets, {
                        markApplied: true,
                    });
                },

                onResponse(response, detail) {
                    root.classList.remove(
                        "has-error"
                    );

                    root.dataset.nostoError =
                        "false";


                    root.dispatchEvent(
                        new CustomEvent(
                            "nosto:carousel-plp:response",
                            {
                                bubbles: true,

                                detail: {
                                    response,
                                    ...detail,
                                },
                            }
                        )
                    );
                },

                onLoadingChange(
                    isLoading,
                    activeController
                ) {
                    const showPageOverlay =
                        isLoading &&
                        activeController?.loadingMode !==
                        "append";

                    root.classList.toggle(
                        "is-loading",
                        showPageOverlay
                    );

                    root.dataset.nostoLoading =
                        String(isLoading);

                    root.dataset.nostoLoadingMode =
                        activeController?.loadingMode ||
                        "replace";

                    root.setAttribute(
                        "aria-busy",
                        String(isLoading)
                    );
                },

                onError(error) {
                    root.classList.add("has-error");
                    root.dataset.nostoError = "true";

                    root.dispatchEvent(
                        new CustomEvent(
                            "nosto:carousel-plp:error",
                            {
                                bubbles: true,
                                detail: {
                                    error,
                                    controller,
                                },
                            }
                        )
                    );
                },
            });

        global.NOSTO_CAROUSEL_PLP = {
            client,
            facetManager,
            hitStore,
            facetRenderer,
            resultsRenderer,
            productViewService,
            quickBuyModal,
            paginationManager,
            controller,
        };

        await controller.init();
        bindFiltersDrawerClose();

        root.dataset.nostoInitialized = "true";
        delete root.dataset.nostoInitializing;

        return global.NOSTO_CAROUSEL_PLP;
    }

    function boot() {
        init().catch((error) => {
            const root = document.querySelector(
                SELECTORS.root
            );

            if (root) {
                delete root.dataset.nostoInitializing;
            }

            console.error(
                "[NostoCarouselPLP] No fue posible inicializar el PLP.",
                error
            );
        });
    }


    document.addEventListener(
        "shopify:section:load",
        (event) => {
            const sectionRoot =
                event.target?.matches?.(
                    SELECTORS.root
                )
                    ? event.target
                    : event.target?.querySelector?.(
                        SELECTORS.root
                    );

            if (!sectionRoot) {
                return;
            }

            delete sectionRoot.dataset
                .nostoInitialized;

            delete sectionRoot.dataset
                .nostoInitializing;

            global.NOSTO_CAROUSEL_PLP =
                null;

            init().catch((error) => {
                console.error(
                    "[NostoCarouselPLP] No fue posible reinicializar la sección.",
                    error
                );
            });
        }
    );

    if (document.readyState === "loading") {
        document.addEventListener(
            "DOMContentLoaded",
            boot,
            { once: true }
        );
    } else {
        boot();
    }
})(window);